import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import CARD_1 from '~/assets/images/vCard/vCard_template_1.png';
import CARD_2 from '~/assets/images/vCard/vCard_template_2.png';
import CARD_3 from '~/assets/images/vCard/vCard_template_3.png';
import CARD_4 from '~/assets/images/vCard/vCard_template_4.png';
import CARD_5 from '~/assets/images/vCard/vCard_template_5.png';
import CARD_0 from '~/assets/images/vCard/vCard_template_default.png';
import MultiColorPicker from '~/components/atoms/color-picker/MultiColorPicker';
import TemplateSlider from '~/components/atoms/template-preview/TemplateSlider';
import Accordion from '~/components/ui/Accordion/Accordion';
import Select from '~/components/ui/Select';
import { DESKTOP_MIN_WIDTH, FONTS } from '~/constants/common';
import { V_CARD_BUILDER_COLORS } from '~/constants/vCard';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { updateVCardPlusSiteThunk, vCardPlusSlice } from '~/store/reducers/previewScreens/vcard-plus';
import { getVCardPlusReducer } from '~/store/reducers/previewScreens/vcard-plus/selectors';

const { actions } = vCardPlusSlice;

const getTemplatesImages = (t) => [
  {
    title: t('other:default'),
    component: CARD_0,
  },
  {
    title: t('other:template', { templateId: 1 }),
    component: CARD_1,
  },
  {
    title: t('other:template', { templateId: 2 }),
    component: CARD_2,
  },
  {
    title: t('other:template', { templateId: 3 }),
    component: CARD_3,
  },
  {
    title: t('other:template', { templateId: 4 }),
    component: CARD_4,
  },
  {
    title: t('other:template', { templateId: 5 }),
    component: CARD_5,
  },
];

export default function Appearance() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));
  const { selectedTemplate, fonts, micrositeData, bacgkroundColor } = useSelector(getVCardPlusReducer);

  const getReplacedInitialColor = () => {
    if (bacgkroundColor) return bacgkroundColor;

    return V_CARD_BUILDER_COLORS[selectedTemplate];
  };

  const setTemplate = (val) => {
    if (micrositeData?.id) {
      dispatch(
        updateVCardPlusSiteThunk({
          id: micrositeData.id,
          body: {
            selected_template: val,
          },
          dispatchHandler: () => dispatch(actions.setSelectedTemplate(val)),
        }),
      );
    }
  };

  const setColorValues = (val) => {
    if (micrositeData?.id) {
      dispatch(
        updateVCardPlusSiteThunk({
          id: micrositeData.id,
          body: {
            design: {
              primary: val[0],
              secondary: val[1],
            },
          },
          dispatchHandler: () => dispatch(actions.setBackgroundColor(val)),
        }),
      );
    }
  };

  const setFontStyles = (e, field) => {
    const value = e.target.value;

    dispatch(
      updateVCardPlusSiteThunk({
        id: micrositeData.id,
        body: {
          fonts: { ...fonts, [field]: value },
        },
        dispatchHandler: () => dispatch(actions.setFonts({ field, value })),
      }),
    );
  };

  return (
    <CreateQRContentBlockLayout type="appearance">
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('basicTemplateLabels:tmplPreview')}
        details={
          <TemplateSlider
            defaultSelectedItem={selectedTemplate}
            images={getTemplatesImages}
            selectSlide={setTemplate}
          />
        }
      />
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('basicTemplateLabels:tmplDesign')}
        details={
          <MultiColorPicker
            initialColors={V_CARD_BUILDER_COLORS}
            selectedTemplate={selectedTemplate}
            selectedColors={[micrositeData?.design?.primary, micrositeData?.design?.secondary]}
            getReplacedInitialColor={getReplacedInitialColor}
            setColorValues={setColorValues}
            onSketchPickerChange={(val) => dispatch(actions.setBackgroundColor(val))}
            blurFunction={(arr) => setColorValues(arr)}
          />
        }
      />
      <Accordion
        defaultExpanded
        variant="child"
        hideBottomBorder
        summary={t('basicTemplateLabels:tmplFonts')}
        details={
          <Box display="flex" flexDirection={isDesktop ? 'row' : 'column'} sx={{ gap: '16px' }}>
            <Select
              items={FONTS}
              onChange={(e) => setFontStyles(e, 'title')}
              fullWidth
              label={t('other:title')}
              defaultValue={fonts?.title || FONTS[3].value}
            />
            <Select
              items={FONTS}
              onChange={(e) => setFontStyles(e, 'text')}
              fullWidth
              label={t('other:text')}
              defaultValue={fonts?.text || FONTS[3].value}
            />
          </Box>
        }
      />
    </CreateQRContentBlockLayout>
  );
}
